import React, { useMemo } from 'react';
import { View, Text, Platform, StyleSheet } from 'react-native';
import KpiModuleCard from '../charts/KpiModuleCard';
import TrendKpiCard from '../charts/TrendKpiCard';
import GaugeChart from '../charts/GaugeChart';
import HeatmapCalendar from '../charts/HeatmapCalendar';
import MiniStackedBarChart from '../charts/MiniStackedBarChart';
import MiniBreachHeatmap from '../charts/MiniBreachHeatmap';
import { calcBreachRate, calcNuisanceFreeDays, calcBreachByPeriod } from '../kpiService';

// NFD: higher % is better — invert zone colours
const NFD_ZONES = [
  { threshold: 33,  color: '#C62828' },
  { threshold: 66,  color: '#F57F17' },
  { threshold: 100, color: '#2E7D32' },
];

interface Props {
  noise:     any[];
  vibration: any[];
  pm10:      any[];
  pm25:      any[];
  stretch?:  boolean;
}

export default function NuisanceModule({ noise, vibration, pm10, pm25, stretch }: Props) {
  const breach         = useMemo(() => calcBreachRate(noise, vibration, pm10, pm25), [noise, vibration, pm10, pm25]);
  const nuisance       = useMemo(() => calcNuisanceFreeDays(noise, vibration, pm10, pm25), [noise, vibration, pm10, pm25]);
  const periodBreaches = useMemo(() => calcBreachByPeriod(noise, vibration, pm10, pm25), [noise, vibration, pm10, pm25]);

  return (
    <KpiModuleCard title="Community & Environmental Nuisance KPIs" accentColor="#E65100" stretch={stretch}>
      <View style={styles.mainRow}>

        {/* 1. Breach Rate */}
        <TrendKpiCard
          label="Community Impact Breach Rate"
          value={breach.totalBreachDays}
          unit="breach days"
          accent="#C62828"
          subtitle={`${breach.breachPercent}% of ${breach.totalMonitoringDays} monitoring days`}
        />

        {/* 2. Nuisance-Free Days — gauge */}
        <View style={styles.gaugeBlock}>
          <Text style={styles.blockLabel}>NUISANCE-FREE DAYS</Text>
          <GaugeChart
            value={nuisance.percent}
            size={100}
            colorZones={NFD_ZONES}
          />
          <Text style={styles.nfdSub}>
            {nuisance.totalBreachDays} breach day{nuisance.totalBreachDays !== 1 ? 's' : ''} detected
          </Text>
        </View>

        {/* 3+4. Breach type breakdown — stacked bar + heatmap */}
        {periodBreaches.length > 0 && (
          <View style={styles.breakdownBlock}>
            <Text style={styles.blockLabel}>BREACH TYPE BREAKDOWN BY PERIOD</Text>
            <View style={styles.chartsRow}>
              <View style={styles.chartInner}>
                <Text style={styles.chartSubTitle}>Stacked by Type</Text>
                <MiniStackedBarChart data={periodBreaches} height={80} />
              </View>
              {Platform.OS === 'web' && (
                <View style={styles.chartInner}>
                  <Text style={styles.chartSubTitle}>Heatmap Grid</Text>
                  <MiniBreachHeatmap data={periodBreaches} />
                </View>
              )}
            </View>
          </View>
        )}

        {/* Monitoring Calendar — inline in the row */}
        {Platform.OS === 'web' && nuisance.calendarData.length > 0 && (
          <View style={styles.calendarBlock}>
            <Text style={styles.blockLabel}>MONITORING CALENDAR</Text>
            <HeatmapCalendar data={nuisance.calendarData} />
          </View>
        )}

      </View>
    </KpiModuleCard>
  );
}

const styles = StyleSheet.create({
  mainRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'flex-start',
  },
  blockLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#777',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  gaugeBlock: {
    flex: 1,
    minWidth: 120,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginVertical: 4,
  },
  nfdSub: {
    fontSize: 10,
    color: '#999',
    textAlign: 'center',
    marginTop: 4,
  },
  breakdownBlock: {
    flex: 3,
    minWidth: 320,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginVertical: 4,
  },
  chartsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'flex-start',
  },
  chartInner: {
    flex: 1,
    minWidth: 140,
  },
  chartSubTitle: {
    fontSize: 9,
    color: '#AAA',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 6,
  },
  calendarBlock: {
    flex: 1,
    minWidth: 160,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginVertical: 4,
  },
});
